import React, { useEffect, useRef, useState } from 'react';
import Navbar from './components/Navbar/Navbar';
import Hero from './components/Hero';
import Speisekarte from './components/Speisekarte';
import UeberUns from './components/UeberUns';
import Kontakt from './components/Kontakt';
import Impressum from './components/Impressum';
import Datenschutz from './components/Datenschutz';
import Footer from './components/Footer';
import './styles.css';

const HOME_ROUTE = '/';
const ABOUT_ROUTE = '/ueber-uns';
const CONTACT_ROUTE = '/kontakt';
const IMPRINT_ROUTE = '/impressum';
const PRIVACY_ROUTE = '/datenschutz';
const ROUTES = [
  HOME_ROUTE,
  ABOUT_ROUTE,
  CONTACT_ROUTE,
  IMPRINT_ROUTE,
  PRIVACY_ROUTE,
];

const ABOUT_ROUTE_ALIASES = ['/uberuns', '/uber-uns', '/über-uns'];

const getCurrentRoute = () => {
  const hashRoute = window.location.hash.replace(/^#/, '');
  const pathRoute = window.location.pathname || HOME_ROUTE;
  const route = hashRoute || pathRoute;

  if (ABOUT_ROUTE_ALIASES.includes(route)) {
    return ABOUT_ROUTE;
  }

  if (ROUTES.includes(route)) {
    return route;
  }

  return HOME_ROUTE;
};

const updateBrowserRoute = (nextRoute) => {
  if (window.location.pathname === nextRoute && !window.location.hash) {
    return;
  }

  window.history.pushState(null, '', nextRoute);
  window.dispatchEvent(new Event('popstate'));
};

const safeScrollToTop = () => {
  if (process.env.NODE_ENV === 'test') {
    return;
  }

  if (typeof window.scrollTo === 'function') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const App = () => {
  const speisekarteRef = useRef(null);
  const pendingScrollRef = useRef(null);
  const [route, setRoute] = useState(getCurrentRoute);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const handleRouteChange = () => {
      setRoute(getCurrentRoute());
    };

    if (
      window.location.hash ||
      ABOUT_ROUTE_ALIASES.includes(window.location.pathname)
    ) {
      window.history.replaceState(null, '', getCurrentRoute());
    }

    window.addEventListener('popstate', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  useEffect(() => {
    if (route === HOME_ROUTE && pendingScrollRef.current === 'speisekarte') {
      window.requestAnimationFrame(() => {
        scrollToSection(speisekarteRef);
      });
      pendingScrollRef.current = null;
    } else {
      safeScrollToTop();
    }
  }, [route]);

  const navigate = (target) => {
    if (target === 'home') {
      pendingScrollRef.current = null;

      if (route === HOME_ROUTE) {
        safeScrollToTop();
        return;
      }

      updateBrowserRoute(HOME_ROUTE);
      return;
    }

    if (target === 'speisekarte') {
      if (route === HOME_ROUTE) {
        scrollToSection(speisekarteRef);
      } else {
        pendingScrollRef.current = 'speisekarte';
        updateBrowserRoute(HOME_ROUTE);
      }
      return;
    }

    if (target === 'ueberUns') {
      pendingScrollRef.current = null;
      updateBrowserRoute(ABOUT_ROUTE);
      return;
    }

    if (target === 'kontakt') {
      pendingScrollRef.current = null;
      updateBrowserRoute(CONTACT_ROUTE);
      return;
    }

    if (target === 'impressum') {
      pendingScrollRef.current = null;
      updateBrowserRoute(IMPRINT_ROUTE);
      return;
    }

    if (target === 'datenschutz') {
      pendingScrollRef.current = null;
      updateBrowserRoute(PRIVACY_ROUTE);
    }
  };

  const renderPage = () => {
    if (route === ABOUT_ROUTE) {
      return (
        <main className="page-main page-main-offset">
          <UeberUns onBackHome={() => navigate('home')} />
        </main>
      );
    }

    if (route === CONTACT_ROUTE) {
      return (
        <main className="page-main page-main-offset">
          <Kontakt onBackHome={() => navigate('home')} />
        </main>
      );
    }

    if (route === IMPRINT_ROUTE) {
      return (
        <main className="page-main page-main-offset">
          <Impressum />
        </main>
      );
    }

    if (route === PRIVACY_ROUTE) {
      return (
        <main className="page-main page-main-offset">
          <Datenschutz />
        </main>
      );
    }

    return (
      <>
        <Hero
          onPrimaryAction={() => navigate('speisekarte')}
          onSecondaryAction={() => navigate('kontakt')}
        />
        <main className="page-main">
          <section id="speisekarte" ref={speisekarteRef} className="section-anchor">
            <Speisekarte onContactClick={() => navigate('kontakt')} />
          </section>
        </main>
      </>
    );
  };

  return (
    <div className="app-shell">
      <Navbar currentRoute={route} onNavigate={navigate} />
      {renderPage()}
      <Footer onNavigate={navigate} />
    </div>
  );
};

export default App;
